/**
 * Symphony GROUP CHAT, Phase 10c — web's calls for typed messages and what each answer does to the group store:
 * sending a photo / video / document / voice message / GIF (mint an upload URL, PUT the bytes to Wix Media with
 * progress, post the file id), sending a poll / event / place / contact / sticker, the short-lived file links the
 * bubbles draw from, and the optimistic poll vote and event answer. Every decision is pure, in `lib/group-content.ts`;
 * this file only moves data. Nothing here names the caller.
 */
import { useEffect, useSyncExternalStore } from 'react';
import type { Message } from './use-chat';
import {
  dropPendingGroupPost,
  groupChatAction,
  groupChatSnapshot,
  groupMessageFromWire,
  handleLostAccess,
  pendingGroupPost,
  postGroupMessage,
  putPendingGroupPost,
  registerTypedUploadRetry,
  replaceGroupMessage,
  showGroupNotice,
} from './group-chat-api';
import { refusalKey, type PendingGroupPost } from './group-chat-actions';
import {
  GROUP_CONTENT_RULES,
  UPLOAD_START,
  linkBatches,
  linkExpiry,
  measuredWithinBounds,
  myPollChoice,
  myRsvp,
  linkState,
  nextUpload,
  uploadErrorKey,
  uploadedMediaId,
  voiceNoteSend,
  withMyRsvp,
  withMyVote,
  type GroupContent,
  type GroupMediaType,
  type GroupRsvp,
  type LinkState,
  type MediaLink,
  type UploadEvent,
  type UploadState,
} from './group-content';

const serverId = (m: Message) => m.finalId ?? m.id ?? '';

// ─── File links ──────────────────────────────────────────────────────────────────────────────────────────────

interface LinkStore {
  links: Record<string, MediaLink>;
  missed: Record<string, number>;
}
let links: LinkStore = { links: {}, missed: {} };
const linkListeners = new Set<() => void>();
const setLinks = (next: LinkStore) => {
  links = next;
  linkListeners.forEach((l) => l());
};
const queued = new Map<string, Set<string>>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const inFlight = new Set<string>();

async function flushLinks() {
  flushTimer = null;
  const work = [...queued.entries()];
  queued.clear();
  for (const [conversationId, ids] of work) {
    const now = Date.now();
    for (const batch of linkBatches([...ids].filter((id) => !inFlight.has(id)), links.links, links.missed, now)) {
      batch.forEach((id) => inFlight.add(id));
      const outcome = await groupChatAction<{ urls?: { messageId: string; url: string; expiresDate: string | null }[] }>('media-urls', { conversationId, messageIds: batch });
      batch.forEach((id) => inFlight.delete(id));
      if (outcome.kind === 'refused') {
        handleLostAccess(conversationId, outcome.refusal);
        continue;
      }
      if (outcome.kind !== 'ok') continue;
      const got: Record<string, MediaLink> = {};
      for (const u of outcome.data.urls ?? []) {
        if (u.messageId && u.url) got[u.messageId] = { url: u.url, expiresAt: linkExpiry(u.expiresDate, Date.now()) };
      }
      const missed = { ...links.missed };
      const at = Date.now();
      for (const id of batch) {
        if (got[id]) delete missed[id];
        else missed[id] = at;
      }
      setLinks({ links: { ...links.links, ...got }, missed });
    }
  }
}

/** Ask for a link to a typed message's file (coalesced over ~100 ms, at most 50 a call). */
export function requestMediaLink(conversationId: string, messageId: string) {
  const set = queued.get(conversationId) ?? new Set<string>();
  set.add(messageId);
  queued.set(conversationId, set);
  if (!flushTimer) flushTimer = setTimeout(() => void flushLinks(), 100);
}

/** A fresh link for a stored typed message (asked for while it is shown, refreshed before it expires), or why not. */
export function useMediaLink(conversationId: string | null | undefined, messageId: string | null | undefined, enabled = true): LinkState {
  const snapshot = useSyncExternalStore(
    (l) => {
      linkListeners.add(l);
      return () => linkListeners.delete(l);
    },
    () => links,
    () => links,
  );
  const state = messageId ? linkState(snapshot.links[messageId], snapshot.missed[messageId], Date.now()) : ({ kind: 'unknown' } as LinkState);
  useEffect(() => {
    if (!enabled || !conversationId || !messageId) return;
    if (state.kind === 'stale' || state.kind === 'unknown') requestMediaLink(conversationId, messageId);
    // Re-ask just before the link runs out, or once the "not available" wait has passed.
    const link = snapshot.links[messageId];
    const missedAt = snapshot.missed[messageId];
    const wait = state.kind === 'fresh' && link ? link.expiresAt - Date.now() - GROUP_CONTENT_RULES.linkFreshMs + 50
      : state.kind === 'unavailable' && missedAt !== undefined ? missedAt + GROUP_CONTENT_RULES.linkRetryMs - Date.now() + 50 : null;
    if (wait === null) return;
    const timer = setTimeout(() => setLinks({ ...links }), Math.max(1000, wait));
    return () => clearTimeout(timer);
  }, [enabled, conversationId, messageId, state.kind, snapshot]);
  return state;
}

/** A link for an action now (open, download): the cached one while fresh, else asked for at once. */
export async function mediaLinkNow(conversationId: string, messageId: string): Promise<string | null> {
  const s = linkState(links.links[messageId], undefined, Date.now());
  if (s.kind === 'fresh') return s.url;
  const outcome = await groupChatAction<{ urls?: { messageId: string; url: string; expiresDate: string | null }[] }>('media-urls', { conversationId, messageIds: [messageId] });
  const u = outcome.kind === 'ok' ? outcome.data.urls?.find((x) => x.messageId === messageId) : undefined;
  if (!u?.url) return null;
  setLinks({ ...links, links: { ...links.links, [messageId]: { url: u.url, expiresAt: linkExpiry(u.expiresDate, Date.now()) } } });
  return u.url;
}

/** Test seam and sign-out: forget every link (links are never persisted). */
export function resetMediaLinks() {
  setLinks({ links: {}, missed: {} });
}

/** The fixture world: answer links from local assets without a backend (dev mock data only). */
export function seedMediaLinks(seeded: Record<string, string>) {
  const far = Date.now() + 24 * 60 * 60 * 1000;
  setLinks({ ...links, links: { ...links.links, ...Object.fromEntries(Object.entries(seeded).map(([id, url]) => [id, { url, expiresAt: far }])) } });
}

// ─── Sending ─────────────────────────────────────────────────────────────────────────────────────────────────

interface Upload {
  file: Blob;
  fileName: string;
  xhr: XMLHttpRequest | null;
}
const uploads = new Map<string, Upload>();

function apply(conversationId: string, clientMessageId: string, event: UploadEvent): PendingGroupPost | undefined {
  const post = pendingGroupPost(conversationId, clientMessageId);
  if (!post?.upload) return post;
  const next: UploadState = nextUpload(post.upload.state, event);
  const failed = next.phase === 'failed';
  const updated: PendingGroupPost = { ...post, state: failed ? 'failed' : 'sending', ...(failed ? { errorKey: next.errorKey ?? 'chat.group.upload.failed' } : { errorKey: undefined }), upload: { ...post.upload, state: next } };
  putPendingGroupPost(updated);
  return updated;
}

function putBytes(url: string, file: Blob, mimeType: string, onProgress: (f: number) => void, holder: Upload): Promise<string | null> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    holder.xhr = xhr;
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', mimeType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) return resolve(null);
      try {
        resolve(uploadedMediaId(JSON.parse(xhr.responseText)));
      } catch {
        resolve(null);
      }
    };
    xhr.onerror = () => resolve(null);
    xhr.onabort = () => resolve(null);
    xhr.send(file);
  });
}

async function runUpload(conversationId: string, clientMessageId: string) {
  const holder = uploads.get(clientMessageId);
  let post = pendingGroupPost(conversationId, clientMessageId);
  if (!holder || !post?.upload || !post.content?.media) return;
  const type = post.content.type as GroupMediaType;
  if (post.upload.state.phase === 'requesting') {
    const minted = await groupChatAction<{ uploadUrl?: string; uploadFileName?: string; uploadMimeType?: string }>('media-upload', {
      conversationId,
      type,
      fileName: holder.fileName,
      mimeType: post.content.media.mimeType,
      sizeBytes: post.content.media.sizeBytes,
    });
    if (pendingGroupPost(conversationId, clientMessageId)?.upload?.state.phase !== 'requesting') return; // cancelled
    if (outcomeLost(conversationId, minted)) return;
    if (minted.kind !== 'ok' || !minted.data.uploadUrl) {
      apply(conversationId, clientMessageId, { kind: 'failed', errorKey: uploadErrorKey(minted.kind === 'refused' ? minted.refusal : null, refusalKey) });
      return;
    }
    apply(conversationId, clientMessageId, { kind: 'minted' });
    const mime = minted.data.uploadMimeType || post.content.media.mimeType;
    const putUrl = `${minted.data.uploadUrl}?filename=${encodeURIComponent(minted.data.uploadFileName || holder.fileName)}`;
    let last = 0;
    const mediaId = await putBytes(putUrl, holder.file, mime, (f) => {
      // Repaint at most every 2%.
      if (f - last >= 0.02 || f === 1) {
        last = f;
        apply(conversationId, clientMessageId, { kind: 'progress', fraction: f });
      }
    }, holder);
    holder.xhr = null;
    if (pendingGroupPost(conversationId, clientMessageId)?.upload?.state.phase !== 'uploading') return; // cancelled
    if (!mediaId) {
      apply(conversationId, clientMessageId, { kind: 'failed', errorKey: 'chat.group.upload.failed' });
      return;
    }
    post = apply(conversationId, clientMessageId, { kind: 'uploaded', mediaId });
    if (post?.content?.media) {
      post = { ...post, content: { ...post.content, media: { ...post.content.media, mediaId, mimeType: mime } } };
      putPendingGroupPost(post);
    }
  }
  post = pendingGroupPost(conversationId, clientMessageId);
  if (!post?.upload || post.upload.state.phase !== 'posting' || !post.content?.media) return;
  const content: GroupContent = { ...post.content, media: { ...post.content.media, mediaId: post.upload.state.mediaId ?? post.content.media.mediaId } };
  const { state: _s, errorKey: _e, upload, ...rest } = post;
  const outcome = await postGroupMessage({ ...rest, content, upload });
  if (outcome.kind === 'ok') {
    uploads.delete(clientMessageId);
    if (upload.localUrl) setTimeout(() => URL.revokeObjectURL(upload.localUrl!), 60_000);
    return;
  }
  // postGroupMessage left the bubble failed; keep the minted file id so a retry only re-posts.
  const failed = pendingGroupPost(conversationId, clientMessageId);
  if (failed?.upload) {
    const errorKey = uploadErrorKey(outcome.kind === 'refused' ? outcome.refusal : null, refusalKey);
    putPendingGroupPost({ ...failed, state: 'failed', errorKey, upload: { ...failed.upload, state: { ...failed.upload.state, phase: 'failed', errorKey } } });
  }
}

function outcomeLost(conversationId: string, outcome: { kind: string; refusal?: string }): boolean {
  return outcome.kind === 'refused' && handleLostAccess(conversationId, (outcome as { refusal: string }).refusal);
}

/**
 * Send one file as a typed message: its bubble shows at once (with the local preview and progress), the bytes go to
 * Wix Media, and the post names the file it got. Checked (type, size) by the caller with `checkMedia` first.
 */
export function sendGroupMedia(input: {
  conversationId: string;
  type: GroupMediaType;
  file: Blob;
  fileName: string;
  mimeType: string;
  caption: string;
  width?: number;
  height?: number;
  durationMs?: number;
  waveform?: number[];
  replyToMessageId?: string;
}): string {
  const clientMessageId = crypto.randomUUID();
  const measured = measuredWithinBounds(input);
  const localUrl = input.type === 'FILE' ? undefined : URL.createObjectURL(input.file);
  uploads.set(clientMessageId, { file: input.file, fileName: input.fileName, xhr: null });
  putPendingGroupPost({
    clientMessageId,
    conversationId: input.conversationId,
    text: input.caption.trim(),
    mentionedMetaSiteIds: [],
    ...(input.replyToMessageId ? { replyToMessageId: input.replyToMessageId } : {}),
    content: {
      type: input.type,
      caption: input.caption.trim(),
      media: {
        mediaId: '',
        mimeType: input.mimeType,
        sizeBytes: input.file.size,
        fileName: input.fileName,
        width: measured.width,
        height: measured.height,
        durationMs: measured.durationMs,
        waveform: input.waveform ?? [],
      },
    },
    upload: { state: UPLOAD_START, ...(localUrl ? { localUrl } : {}) },
    createdDate: new Date().toISOString(),
    state: 'sending',
  });
  void runUpload(input.conversationId, clientMessageId);
  return clientMessageId;
}

/**
 * Send a recorded voice note into a group — THE one path for the composer's mic and Attach → Voice message alike
 * (`voiceNoteSend` decides, `sendGroupMedia` sends: media-upload → PUT → post-message with VOICE_NOTE content). Null
 * when it went out; else the message to show (too short, a type or size the group refuses).
 */
export function sendGroupVoiceNote(conversationId: string, rec: { blob: Blob; mimeType: string; durationMs: number; samples: readonly number[] }): { key: string; vars?: Record<string, string | number> } | null {
  const send = voiceNoteSend({ sizeBytes: rec.blob.size, mimeType: rec.mimeType, durationMs: rec.durationMs, samples: rec.samples });
  if (!send.ok) return { key: send.key, ...(send.vars ? { vars: send.vars } : {}) };
  sendGroupMedia({ conversationId, type: send.type, file: rec.blob, fileName: send.fileName, mimeType: send.mimeType, caption: '', durationMs: send.durationMs, waveform: send.waveform });
  return null;
}

/** Cancel a file still uploading: the PUT is aborted and the bubble goes away. */
export function cancelGroupUpload(conversationId: string, clientMessageId: string) {
  const post = pendingGroupPost(conversationId, clientMessageId);
  if (!post?.upload) return;
  const next = nextUpload(post.upload.state, { kind: 'cancel' });
  if (next.phase !== 'cancelled') return;
  putPendingGroupPost({ ...post, upload: { ...post.upload, state: next } });
  uploads.get(clientMessageId)?.xhr?.abort();
  uploads.delete(clientMessageId);
  if (post.upload.localUrl) URL.revokeObjectURL(post.upload.localUrl);
  dropPendingGroupPost(conversationId, clientMessageId);
}

registerTypedUploadRetry((conversationId, clientMessageId) => {
  const post = pendingGroupPost(conversationId, clientMessageId);
  if (!post?.upload) return;
  const next = nextUpload(post.upload.state, { kind: 'retry' });
  if (next.phase === 'failed') return;
  if (next.phase === 'requesting' && !uploads.has(clientMessageId)) return; // the bytes are gone (a reload)
  putPendingGroupPost({ ...post, state: 'sending', errorKey: undefined, upload: { ...post.upload, state: next } });
  void runUpload(conversationId, clientMessageId);
});

/** Send a poll, an event, a place, a contact or a sticker (no file): the ordinary post path, with its contentItem. */
export function sendGroupContent(conversationId: string, content: GroupContent, replyToMessageId?: string) {
  return postGroupMessage({
    clientMessageId: crypto.randomUUID(),
    conversationId,
    text: '',
    mentionedMetaSiteIds: [],
    content,
    ...(replyToMessageId ? { replyToMessageId } : {}),
    createdDate: new Date().toISOString(),
  });
}

/** The row as the store holds it now (a replacement made since the tap, e.g. another member's vote). */
function currentRow(conversationId: string, messageId: string): Message | undefined {
  return groupChatSnapshot().replaced[conversationId]?.[messageId];
}

// ─── Votes and answers ───────────────────────────────────────────────────────────────────────────────────────

/** Vote (or change or withdraw): optimistic on the row, replaced by the answer's row, put back on a refusal. */
export async function voteInGroupPoll(conversationId: string, row: Message, viewerId: string, optionIds: string[]): Promise<boolean> {
  const messageId = serverId(row);
  const before: Message = { ...row, id: messageId, conversationId };
  const meta = row.groupMeta ?? {};
  replaceGroupMessage({ ...before, groupMeta: { ...meta, pollVotes: withMyVote(meta.pollVotes ?? [], viewerId, optionIds) } });
  const outcome = await groupChatAction('poll-vote', { conversationId, messageId, optionIds });
  const answered = outcome.kind === 'ok' ? groupMessageFromWire(outcome.data.message) : null;
  if (answered) {
    replaceGroupMessage({ ...answered, conversationId: answered.conversationId ?? conversationId });
    return true;
  }
  // Put back only the viewer's own vote, on the row as it is NOW (others' votes may have landed meanwhile).
  const now = currentRow(conversationId, messageId) ?? before;
  replaceGroupMessage({ ...now, groupMeta: { ...(now.groupMeta ?? {}), pollVotes: withMyVote(now.groupMeta?.pollVotes ?? [], viewerId, myPollChoice(meta.pollVotes ?? [], viewerId)) } });
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return false;
  showGroupNotice(outcome.kind === 'refused' ? refusalKey(outcome.refusal) : 'chat.group.poll.voteFailed');
  return false;
}

/** Answer an event (NO_RESPONSE withdraws): optimistic like the vote. */
export async function respondToGroupEvent(conversationId: string, row: Message, viewerId: string, response: GroupRsvp | 'NO_RESPONSE'): Promise<boolean> {
  const messageId = serverId(row);
  const before: Message = { ...row, id: messageId, conversationId };
  const meta = row.groupMeta ?? {};
  replaceGroupMessage({ ...before, groupMeta: { ...meta, eventRsvps: withMyRsvp(meta.eventRsvps ?? [], viewerId, response) } });
  const outcome = await groupChatAction('event-rsvp', { conversationId, messageId, response });
  const answered = outcome.kind === 'ok' ? groupMessageFromWire(outcome.data.message) : null;
  if (answered) {
    replaceGroupMessage({ ...answered, conversationId: answered.conversationId ?? conversationId });
    return true;
  }
  const now = currentRow(conversationId, messageId) ?? before;
  const previous = myRsvp(meta.eventRsvps ?? [], viewerId);
  replaceGroupMessage({ ...now, groupMeta: { ...(now.groupMeta ?? {}), eventRsvps: withMyRsvp(now.groupMeta?.eventRsvps ?? [], viewerId, previous ?? 'NO_RESPONSE') } });
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return false;
  showGroupNotice(outcome.kind === 'refused' ? refusalKey(outcome.refusal) : 'chat.group.eventCard.rsvpFailed');
  return false;
}
